import {
  isLexicalJsonString,
  parseAndExtractTextFromLexicalJson,
} from 'components/DSYS/editor/utils/lexical';

/**
 * Key on `WorkstreamTableEmbed` dynamic node `value` whose value is a JSON string:
 * `Record<string, string>` mapping entity ids to serialized Lexical (incl. comment highlights).
 * Planning-editor state for each row, stored on the embed node alongside `v`.
 */
export const WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY = 'editorStateMap' as const;

/**
 * Sibling-key prefix that stores the `fallbackText` snapshot a persisted Lexical entry was
 * written for. `PlanningCommentField` uses this to invalidate stale entries when the
 * underlying server-derived text has changed since the snapshot was taken.
 */
export const SOURCE_TEXT_KEY_PREFIX = '__src:' as const;

export function sourceTextKeyFor(mapKey: string): string {
  return `${SOURCE_TEXT_KEY_PREFIX}${mapKey}`;
}

/**
 * Merges one key into a JSON `editorStateMap` string (`Record<string, string>`), and
 * optionally writes (or clears) a sibling source-text snapshot at `__src:${mapKey}` in the
 * same merge. Atomicity matters because callers persist via a single `setValue`; two
 * sequential merges would race.
 *
 * Table embeds and object-shaped nodes (`mergeEditorStateMapIntoObjectFieldWithSource`)
 * both use this for the string merge step.
 */
export function mergeEditorStateMapJsonWithSource(
  currentJson: string | undefined,
  mapKey: string,
  serializedState: string | undefined,
  sourceText: string | undefined
): string | undefined {
  const map: Record<string, string> = {};
  if (currentJson) {
    try {
      Object.assign(map, JSON.parse(currentJson) as Record<string, string>);
    } catch {
      /* ignore */
    }
  }
  const srcKey = sourceTextKeyFor(mapKey);
  if (!serializedState) {
    delete map[mapKey];
    delete map[srcKey];
  } else {
    map[mapKey] = serializedState;
    if (sourceText !== undefined) {
      map[srcKey] = sourceText;
    } else {
      delete map[srcKey];
    }
  }
  if (Object.keys(map).length === 0) {
    return undefined;
  }
  return JSON.stringify(map);
}

export function mergeEditorStateMapIntoObjectFieldWithSource<T extends Record<string, unknown>>(
  base: T,
  field: keyof T & string,
  mapKey: string,
  serializedState: string | undefined,
  sourceText: string | undefined
): T {
  const currentJson = typeof base[field] === 'string' ? (base[field] as string) : undefined;
  const merged = mergeEditorStateMapJsonWithSource(
    currentJson,
    mapKey,
    serializedState,
    sourceText
  );
  const next = {...base} as Record<string, unknown>;
  if (merged === undefined) {
    delete next[field];
  } else {
    next[field] = merged;
  }
  return next as T;
}

export function getEditorStateMapEntry(
  editorStateMapJson: string | undefined,
  mapKey: string
): string | undefined {
  if (!editorStateMapJson) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(editorStateMapJson) as Record<string, string | undefined>;
    const entry = parsed[mapKey];
    return typeof entry === 'string' ? entry : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolves editor state from editorStateMap with staleness checking.
 *
 * Use this when editorStateMap entries (which may include comment highlights) need to be
 * validated against canonical server data. If the underlying content changed in the DB,
 * stale comments are discarded and the fresh fallback value is returned
 *
 * Automatically detects whether fallback is rich Lexical JSON or plain text.
 * For staleness comparison, extracts plain text from rich fallback or uses plain text directly
 *
 * Staleness detection:
 * - If `__src:key` stamp exists: compares stamp to fallback text
 * - If no stamp (legacy entries): extracts plain text from entry and compares to fallback
 * - Returns entry if fresh, fallback if stale/missing
 *
 * @param editorStateMapJson - JSON string containing the editor state map
 * @param mapKey - Key to look up in the map
 * @param fallbackValue - Value to return if entry is missing or stale (plain or rich text)
 * @returns Entry from map (if fresh) or fallbackValue (if stale/missing)
 */
export function resolveStampedEditorStateMapValue(
  editorStateMapJson: string | undefined,
  mapKey: string,
  fallbackValue: string
): string {
  if (!editorStateMapJson) {
    return fallbackValue;
  }

  try {
    const map = JSON.parse(editorStateMapJson) as Record<string, string>;
    const entry = map[mapKey];
    const stampedSourceText = map[sourceTextKeyFor(mapKey)];

    // Automatically detect if fallback is rich text and extract plain text for comparison
    const comparisonText = isLexicalJsonString(fallbackValue)
      ? parseAndExtractTextFromLexicalJson(fallbackValue).trim()
      : fallbackValue;

    // If no stamp exists, extract plain text from entry and compare to fallback.
    // This handles legacy entries created before source text stamping was added
    if (entry !== undefined && stampedSourceText === undefined) {
      const entryPlainText = parseAndExtractTextFromLexicalJson(entry).trim();
      return entryPlainText === comparisonText ? entry : fallbackValue;
    }

    // Check if entry is fresh by comparing stamped source text
    const entryIsFresh = entry !== undefined && stampedSourceText === comparisonText;

    return entryIsFresh ? entry : fallbackValue;
  } catch {
    return fallbackValue;
  }
}
